import React from "react";
import { Card, CardBody, Row, Col, FormGroup, Label, Button } from "reactstrap";
import { useMutation, useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  USER_DETAIL_QUERY,
  USER_EDIT_MUTATION,
  USER_LIST_QUERY,
} from "../../config/Queries";

function formatDateForInputFromMs(ms) {
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function parseDateInputToLocalMs(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).split("-").map((v) => parseInt(v, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const ms = new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function PatientEdit({ match }) {
  const history = useHistory();
  const userCode = parseInt(match.params.id);
  const isValidUserCode = Number.isFinite(userCode);

  const { data, loading } = useQuery(USER_DETAIL_QUERY, {
    skip: !isValidUserCode,
    variables: { code: userCode },
  });

  const [editAccount, { loading: saving }] = useMutation(USER_EDIT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: () => [
      { query: USER_DETAIL_QUERY, variables: { code: userCode } },
      // PatientList에서 사용하는 variables와 동일하게 맞춰야 즉시 반영됩니다.
      { query: USER_LIST_QUERY, variables: { type: "환자" } },
    ],
  });

  const { register, handleSubmit } = useForm();

  const onSubmit = async (form) => {
    try {
      if (!isValidUserCode) {
        toast.error("잘못된 회원 코드입니다. 목록에서 다시 선택해주세요.", {
          autoClose: 3000,
          position: toast.POSITION.TOP_RIGHT,
        });
        history.push("/admin/patients");
        return;
      }

      const current = data?.viewProfile;
      if (!current) {
        toast.error("회원 정보를 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.", {
          autoClose: 3000,
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }

      const nextUserName = (form.userName || "").trim();
      const nextPhone = (form.phone || "").trim();
      const nextPassword = (form.password || "").trim();
      const nextJoinDateMs = parseDateInputToLocalMs(form.createdAtDate);
      const nextResidentNumber = (form.residentNumber || "").trim();

      const variables = { userCode };
      if (current?.userName !== nextUserName) variables.userName = nextUserName;
      if (current?.phone !== nextPhone) variables.phone = nextPhone;
      if (nextPassword) variables.password = nextPassword;
      if ((current?.residentNumber || "") !== nextResidentNumber) {
        variables.residentNumber = nextResidentNumber || null;
      }
      if (
        Number.isFinite(nextJoinDateMs) &&
        parseInt(current?.createdAt, 10) !== nextJoinDateMs
      ) {
        variables.createdAt = String(nextJoinDateMs);
      }

      if (Object.keys(variables).length === 1) {
        toast.info("변경된 내용이 없습니다.", {
          autoClose: 2500,
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }

      const { data: mutationData } = await editAccount({ variables });

      if (!mutationData?.editAccount?.ok) {
        throw new Error(mutationData?.editAccount?.error || "수정에 실패했습니다.");
      }

      toast.success("환자 회원 정보 수정이 완료되었습니다.", {
        autoClose: 3000,
        position: toast.POSITION.TOP_RIGHT,
      });
      history.push(`/admin/patients/${userCode}`);
    } catch (e) {
      toast.error(e?.message || "환자 회원 수정 중 오류가 발생했습니다.", {
        autoClose: 3000,
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  if (!isValidUserCode) return <></>;
  if (loading) return <></>;

  return (
    <div className="content">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <div className="card-heading">환자 회원 정보 수정</div>
          <CardBody>
            <FormGroup row>
              <Label for="userId" sm={2} className="control-label">
                아이디
              </Label>
              <Col sm={10}>
                <input
                  id="userId"
                  name="userId"
                  className="form-control"
                  type="text"
                  disabled
                  defaultValue={data?.viewProfile?.userId}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="userName" sm={2} className="control-label">
                이름
              </Label>
              <Col sm={10}>
                <input
                  id="userName"
                  name="userName"
                  className="form-control"
                  placeholder="이름을 입력해주세요."
                  type="text"
                  defaultValue={data?.viewProfile?.userName}
                  ref={register({ required: "이름을 입력해주세요." })}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="phone" sm={2} className="control-label">
                연락처
              </Label>
              <Col sm={10}>
                <input
                  id="phone"
                  name="phone"
                  className="form-control"
                  placeholder="숫자만 입력 (예: 01012345678)"
                  type="text"
                  defaultValue={data?.viewProfile?.phone}
                  ref={register({ required: "연락처를 입력해주세요." })}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="residentNumber" sm={2} className="control-label">
                주민등록번호
              </Label>
              <Col sm={10}>
                <input
                  id="residentNumber"
                  name="residentNumber"
                  className="form-control"
                  placeholder="주민등록번호"
                  type="text"
                  defaultValue={data?.viewProfile?.residentNumber || ""}
                  ref={register()}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="password" sm={2} className="control-label">
                비밀번호
              </Label>
              <Col sm={10}>
                <input
                  id="password"
                  name="password"
                  className="form-control"
                  placeholder="변경 시에만 입력"
                  type="password"
                  ref={register()}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="createdAtDate" sm={2} className="control-label">
                가입일
              </Label>
              <Col sm={10}>
                <input
                  id="createdAtDate"
                  name="createdAtDate"
                  className="form-control"
                  type="date"
                  defaultValue={formatDateForInputFromMs(
                    parseInt(data?.viewProfile?.createdAt, 10)
                  )}
                  ref={register()}
                />
              </Col>
            </FormGroup>
          </CardBody>
        </Card>

        <Row>
          <Col xs={4} className="text-left">
            <Button
              onClick={() => history.push(`/admin/patients/${userCode}`)}
              className="btn-white"
              type="button"
              disabled={saving}
            >
              <i className="fa fa-list"></i>
              돌아가기
            </Button>
          </Col>
          <Col xs={4} className="text-center">
            <Button type="submit" className="btn-inverse" disabled={saving}>
              <i className="fa fa-check"></i>
              확인
            </Button>
          </Col>
          <Col xs={4} className="text-right"></Col>
        </Row>
      </form>
    </div>
  );
}

export default PatientEdit;

